import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  UserPlus,
  Loader2,
  Info,
  Calendar,
  ShoppingBag,
  BadgePercent,
  PiggyBank,
  Heart,
  Eye,
  Shield,
  Smile
} from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import { NICHE_IMAGES } from '../../utils/constants';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { groupOverviewApi } from '../../services/api';

// Default icons for How it Works (constant, not from API)
const HOW_IT_WORKS_ICONS = [ShoppingBag, BadgePercent, PiggyBank];

// Default icons for Group Rules (constant, not from API)
const GROUP_RULES_ICONS = [Heart, Eye, Shield, Smile];

// Format date as "Apr 26, 2026"
const formatDate = (date) => {
  if (!date) return 'Recently';
  try {
    const d = new Date(date);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  } catch {
    return 'Recently';
  }
};

export default function GroupOverviewDrawer() {
  const navigate = useNavigate();
  const {
    selectedGroup,
    showGroupOverviewDrawer,
    setShowGroupOverviewDrawer,
    joinGroup,
  } = useAppState();
  const { isGuestMode, setShowGuestPrompt } = useAuth();

  const [joining, setJoining] = useState(false);
  const [loading, setLoading] = useState(true);
  const [overviewContent, setOverviewContent] = useState(null);

  // Lock body scroll when drawer is open
  useBodyScrollLock(showGroupOverviewDrawer && !!selectedGroup);

  // Fetch overview content when drawer opens or group changes
  useEffect(() => {
    if (showGroupOverviewDrawer && selectedGroup) {
      const groupId = selectedGroup.uuid || selectedGroup.id;
      if (groupId) {
        setLoading(true);
        groupOverviewApi.getOverview(groupId)
          .then((content) => {
            setOverviewContent(content);
          })
          .catch((error) => {
            console.error('Failed to fetch group overview:', error);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  }, [showGroupOverviewDrawer, selectedGroup]);

  const handleClose = () => {
    setShowGroupOverviewDrawer(false);
  };

  const handleJoinGroup = () => {
    if (!selectedGroup) return;

    // Guest users need to sign up
    if (isGuestMode) {
      setShowGuestPrompt(true);
      return;
    }

    setJoining(true);

    const groupId = selectedGroup.uuid || selectedGroup.id;

    // Join group locally (adds to joined list)
    joinGroup(groupId);

    // Close drawer and navigate to chat
    setShowGroupOverviewDrawer(false);
    navigate(`/groups/${groupId}`);
  };

  if (!showGroupOverviewDrawer || !selectedGroup) return null;

  const memberCount = selectedGroup.members?.length || selectedGroup.member_count || selectedGroup.members_count || 0;
  const dealsCount = selectedGroup.deals?.length || selectedGroup.merchants?.length || selectedGroup.deals_count || 0;
  const isBonndleGroup = (selectedGroup.name || '').toLowerCase().includes('bonndle');
  const groupImage = isBonndleGroup
    ? '/logo/bonndleLogoIcon_Indigo.svg'
    : selectedGroup.image_url || selectedGroup.avatar || selectedGroup.image || NICHE_IMAGES[selectedGroup.niche]?.[0] || NICHE_IMAGES.general[0];

  // Use API content or fallback to defaults
  const aboutDescription = overviewContent?.aboutDescription || selectedGroup.description;
  const howItWorks = overviewContent?.howItWorks || [];
  const groupRules = overviewContent?.groupRules || [];

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Drawer - 50% height, with padding for fixed bottom button */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl h-[50vh] flex flex-col animate-slide-up pb-[88px]">
        {/* Drag Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-4 pb-4 flex items-start gap-4">
          {/* Group Avatar */}
          <div className="w-16 h-16 bg-gray-100 rounded-2xl p-2 flex-shrink-0">
            <img
              src={groupImage}
              alt={selectedGroup.name}
              className="w-full h-full object-contain rounded-xl"
              onError={(e) => {
                e.target.src = NICHE_IMAGES.general[0];
              }}
            />
          </div>

          {/* Group Info */}
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-xl text-gray-900 truncate">
              {selectedGroup.name}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {memberCount} members{dealsCount > 0 ? ` • ${dealsCount} Active Deals` : ''}
            </p>
          </div>

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="border-t border-gray-100" />

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-indigo-600" />
            </div>
          ) : (
            <>
              {/* About This Group */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-bold text-gray-900 mb-3">About This Group</h3>

                {aboutDescription && (
                  <div className="flex items-start gap-2 mb-2">
                    <Info size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-500">{aboutDescription}</span>
                  </div>
                )}

                <div className="flex items-start gap-2">
                  <Calendar size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-500">
                    Created: {formatDate(selectedGroup.created_at || selectedGroup.createdAt)}
                  </span>
                </div>
              </div>

              {/* How it Works */}
              {howItWorks.length > 0 && (
                <div className="bg-white border border-gray-100 rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 mb-3">How it Works</h3>

                  <div className="space-y-2">
                    {howItWorks.map((text, index) => {
                      const Icon = HOW_IT_WORKS_ICONS[index] || ShoppingBag;
                      return (
                        <div key={index} className="flex items-start gap-2">
                          <Icon size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-500">{text}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Group Rules */}
              {groupRules.length > 0 && (
                <div className="bg-white border border-gray-100 rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 mb-3">Group Rules</h3>

                  <div className="space-y-2">
                    {groupRules.map((rule, index) => {
                      const Icon = GROUP_RULES_ICONS[index] || Heart;
                      return (
                        <div key={index} className="flex items-start gap-2">
                          <Icon size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-500">
                            <strong>{rule.title}.</strong> {rule.description}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

      </div>

      {/* Join Button - Fixed at bottom like BottomNav */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 z-[60] px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <button
          onClick={handleJoinGroup}
          disabled={joining}
          className="w-full py-4 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {joining ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <>
              <UserPlus size={20} />
              Join Group
            </>
          )}
        </button>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
